import {FluentNumber} from "@fluent/bundle";
import {Localized} from "@fluent/react";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Rating,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import React, {useCallback, useContext, useEffect, useState} from "react";
import {useLocation} from "react-router-dom";

import AssessmentDeliveryContext from "../AssessmentDeliveryContext";
import {requiresExplicitSessionStart} from "../../store/chapters/Chapter";
import selectChapter from "../../store/chapters/selectChapter";
import CourseType from "../../store/courses/CourseType";
import selectCourse from "../../store/courses/selectCourse";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {keyProvider} from "../../store/keyProvider";
import {analyticsService} from "../../store/services/analyticsService";
import selectChapterCompletionRewardStatus from "../../store/ui/selectChapterCompletionRewardStatus";
import {markChapterCompletionRewardShown} from "../../store/ui/chapterCompletionRewardSlice";

function ChapterCompletionDialog(props: {
	studentId: number;
	courseId: number;
	chapterId: number;
}) {
	const chapterKey = keyProvider.chapter(props.chapterId);

	const status = useAppSelector((state) =>
		selectChapterCompletionRewardStatus(state, chapterKey)
	);

	const dispatch = useAppDispatch();
	const location = useLocation();

	const markShownAndClose = useCallback(() => {
		dispatch(markChapterCompletionRewardShown({chapterKey}));
	}, [chapterKey, dispatch]);

	const contentPage = location.pathname.includes("/sections/");
	const showing = status === "showing";

	useEffect(() => {
		if (!contentPage && showing) {
			markShownAndClose();
		}
	}, [contentPage, markShownAndClose, showing]);

	return (
		<Dialog
			open={contentPage && showing}
			hideBackdrop
			disableScrollLock
			onClose={markShownAndClose}
		>
			<RewardDialogContent
				studentId={props.studentId}
				courseId={props.courseId}
				chapterKey={chapterKey}
				onClose={markShownAndClose}
			/>
		</Dialog>
	);
}

function RewardContent(props: {
	studentId: number;
	courseId: number;
	chapterKey: string;
	onClose: () => void;
}) {
	const {chapterKey} = props;

	const {selectAllChapterOutcomes, selectChapterContentStats} = useContext(
		AssessmentDeliveryContext
	);

	const chapter = useAppSelector((state) => selectChapter(state, chapterKey));
	if (!chapter) {
		throw new Error("Chapter not found");
	}

	const courseType = useAppSelector(
		(state) =>
			selectCourse(state, keyProvider.course(props.courseId))?.type ??
			CourseType.Ordinary
	);

	const [outcomes] = useAppSelector((state) =>
		selectAllChapterOutcomes(state, chapterKey)
	);

	const [, maxScore] = useAppSelector((state) =>
		selectChapterContentStats(state, chapterKey)
	);

	const [completionTimePercentile, setCompletionTimePercentile] = useState(0);

	useEffect(() => {
		analyticsService
			.getStudentChapterSessionSuccessMetrics(
				props.studentId,
				props.courseId,
				chapter.id
			)
			.then((res) => {
				setCompletionTimePercentile(res.completionTimePercentile);
			});
	}, [chapter.id, props.courseId, props.studentId]);

	const score = outcomes.reduce((sum, o) => sum + o.score, 0);

	const scorePercentage = (score / maxScore) * 100;

	const type = requiresExplicitSessionStart(courseType, chapter)
		? "test"
		: "chapter";

	return (
		<>
			<DialogContent>
				<Stack
					direction="column"
					spacing={2}
					alignItems="center"
					sx={{textAlign: "center"}}
				>
					<Rating
						value={scorePercentage / 20}
						precision={0.5}
						readOnly
						sx={{fontSize: (theme) => theme.spacing(6)}}
					/>

					<Typography variant="h5">
						<Localized id="chapter-completion-dialog-title" vars={{type}}>
							Chapter completed
						</Localized>
					</Typography>

					<Stack spacing={2} sx={{alignItems: "flex-start"}}>
						<Typography>
							<Localized
								id="chapter-completion-dialog-completion-message"
								vars={{title: chapter.title, number: chapter.number, type}}
							>
								{`You ve finished chapter ${chapter.number} '${chapter.title}'!`}
							</Localized>
						</Typography>

						<Stack direction="row" spacing={1} sx={{alignItems: "center"}}>
							<Typography>
								<Localized
									id="chapter-completion-dialog-score-message"
									vars={{score, maxScore, type}}
								>
									{`You scored ${score} / ${maxScore} points in the chapter.`}
								</Localized>
							</Typography>

							<Tooltip
								title={
									<Localized id="chapter-completion-dialog-completion-message-hint">
										Score may change later by teacher review.
									</Localized>
								}
							>
								<InfoIcon color="action" fontSize="inherit" />
							</Tooltip>
						</Stack>

						{completionTimePercentile >= 50 && (
							<Typography>
								<Localized
									id="chapter-completion-dialog-speed-message"
									vars={{
										percentage: new FluentNumber(
											completionTimePercentile,
											percentFormat
										),
									}}
								>
									{`You were faster than ${completionTimePercentile}% of students.`}
								</Localized>
							</Typography>
						)}
					</Stack>
				</Stack>
			</DialogContent>

			<DialogActions>
				<Button onClick={props.onClose}>
					<Localized id="chapter-completion-dialog-action-ok">OK</Localized>
				</Button>
			</DialogActions>
		</>
	);
}

const percentFormat = {maximumFractionDigits: 0};

const RewardDialogContent = React.memo(RewardContent);

export default ChapterCompletionDialog;
